import { z } from "zod";
import { timestampToDateNullable } from "@/core/firebase/converters";
import { ClubPermissionSchema, type ClubPermission } from "@/core/models/staff";

/**
 * Club role document. Stored at `clubs/{clubId}/roles/{roleId}`.
 *
 * System roles (`owner`, `manager`, `receptionist`) are seeded on club
 * creation and cannot be deleted. The owner role always has all permissions.
 */

/**
 * Bumped whenever `DEFAULT_CLUB_ROLES` gains a permission key on a system role.
 * `reconcileSystemRolePermissions` (run by `useCurrentRole`) uses it to heal
 * stored docs: untouched system roles are snapped to the exact default set,
 * customised roles get the new keys merged in additively (their grants/removals
 * preserved). When the owner signs in, every system role is reconciled +
 * written back; non-owner sessions reconcile in-memory only (Firestore rules
 * allow only owner/admin to write `roles/{id}`). Pre-existing docs (no field)
 * read as `0`.
 *
 * Bump log:
 * - v1 (2026-05-27): added `recordPayment`, `issueRefund`, `viewIngresos`,
 *   `createSale`, `refundSale`, `manageCatalog`, `checkInBooking`.
 * - v2 (2026-06-13): receptionist default gains `issueRefund`, `refundSale`
 *   — reception handles refunds at the desk by default. (The Ingresos ledger
 *   `viewIngresos` stays owner/manager.) The bump propagates the keys into
 *   customised receptionist docs too (additive).
 * - v3 (2026-06-22): receptionist default gains `exportData` — the desk can
 *   export the CSVs it already reads (Reservas / Clientes / Recurrentes /
 *   Actividad). Additive; an owner who removed it keeps it removed.
 */
export const CURRENT_PERMISSIONS_SCHEMA_VERSION = 3;

/**
 * `permissions` is stored as `string[]` (each entry = `ClubPermission.name`).
 * Unknown strings are dropped — match `whereType<ClubPermission>()`.
 */
const permissionsArraySchema = z
  .preprocess((value) => {
    if (!Array.isArray(value)) return [];
    return value.filter((v): v is string => typeof v === "string");
  }, z.array(z.string()))
  .transform((raw) => {
    const parsed: ClubPermission[] = [];
    for (const s of raw) {
      const res = ClubPermissionSchema.safeParse(s);
      if (res.success) parsed.push(res.data);
    }
    return parsed;
  });

export const ClubRoleSchema = z.object({
  /** Document ID. System roles: 'owner', 'manager', 'receptionist'. */
  id: z.string(),
  /** Display name, e.g. "Propietario", "Entrenador". */
  name: z.string().default(""),
  description: z.string().default(""),
  /** Permissions granted by this role. */
  permissions: permissionsArraySchema.default([]),
  /** 0 = highest (owner). Higher number = lower rank. */
  priority: z.number().int().default(99),
  /** System roles cannot be deleted. Owner role cannot be edited. */
  isSystem: z.boolean().default(false),
  /** Optional hex color for UI badges (e.g. '#FF6B35'). */
  color: z.string().nullable().optional(),
  /**
   * True when the owner has explicitly customised this role's permissions.
   * When true, the self-healing upsert flow preserves the current permissions
   * instead of resetting them to code-level defaults.
   */
  permissionsCustomized: z.boolean().default(false),
  /**
   * Schema-version watermark — drives `reconcileSystemRolePermissions`. For
   * untouched roles it gates a snap-to-defaults; for customised roles it gates
   * the additive merge of newly-introduced default keys. Pre-existing docs (no
   * field) read 0.
   */
  permissionsSchemaVersion: z.number().int().nonnegative().default(0),
  createdAt: timestampToDateNullable.nullable().optional(),
  updatedAt: timestampToDateNullable.nullable().optional(),
});

export type ClubRole = z.infer<typeof ClubRoleSchema>;

/** Whether this role has the given permission. */
export function roleCan(role: ClubRole, permission: ClubPermission): boolean {
  return role.permissions.includes(permission);
}

/** Convenience: true if this is the owner role. */
export function isOwnerRole(role: ClubRole): boolean {
  return role.id === "owner";
}

/**
 * The system roles seeded on club creation (owner / manager / receptionist /
 * coach). Used as fallback when the `roles` subcollection hasn't been created
 * yet, and by `defaultRoleById` so a role resolves even without a Firestore
 * doc (how existing clubs pick up the `coach` role with no migration).
 */
export const DEFAULT_CLUB_ROLES: ClubRole[] = [
  {
    id: "owner",
    name: "Propietario",
    description:
      "Acceso completo. Gestiona personal, facturación y configuración del club.",
    permissions: [
      "manageStaff",
      "viewStaff",
      "manageBilling",
      "editClubProfile",
      "manageFeatures",
      "manageCourts",
      "createBooking",
      "cancelBooking",
      "checkInBooking",
      "deleteBooking",
      "viewCrm",
      "exportData",
      "viewCalendar",
      "viewDashboard",
      "viewActivityLog",
      "viewAnalytics",
      "recordPayment",
      "issueRefund",
      "viewIngresos",
      "createSale",
      "refundSale",
      "manageCatalog",
    ],
    priority: 0,
    isSystem: true,
    color: "#FF6B35",
    permissionsCustomized: false,
    permissionsSchemaVersion: CURRENT_PERMISSIONS_SCHEMA_VERSION,
  },
  {
    id: "manager",
    name: "Gerente",
    description:
      "Gestiona reservas, pistas y clientes. No puede cambiar roles ni ver facturación.",
    permissions: [
      "viewStaff",
      "editClubProfile",
      "manageFeatures",
      "manageCourts",
      "createBooking",
      "cancelBooking",
      "checkInBooking",
      "deleteBooking",
      "viewCrm",
      "exportData",
      "viewCalendar",
      "viewDashboard",
      "viewActivityLog",
      "viewAnalytics",
      "recordPayment",
      "issueRefund",
      "viewIngresos",
      "createSale",
      "refundSale",
      "manageCatalog",
    ],
    priority: 1,
    isSystem: true,
    color: "#4FC3F7",
    permissionsCustomized: false,
    permissionsSchemaVersion: CURRENT_PERMISSIONS_SCHEMA_VERSION,
  },
  {
    id: "receptionist",
    name: "Recepcionista",
    description:
      "Crea y cancela reservas, consulta clientes y el registro de actividad.",
    permissions: [
      "createBooking",
      "cancelBooking",
      "checkInBooking",
      "viewCrm",
      "viewCalendar",
      "viewDashboard",
      "viewActivityLog",
      "recordPayment",
      "createSale",
      // Reception handles refunds at the desk by default (booking + sale) —
      // in a small club the same person who cobra also devuelve. The owner can
      // revoke this in Ajustes → Preferencias → Reembolsos. The Ingresos *ledger*
      // (`viewIngresos`) stays owner/manager by default — reception doesn't
      // need the full money book to do a refund; the owner can still grant it
      // per-club in Ajustes → Visibilidad de Ingresos.
      "issueRefund",
      "refundSale",
      // The desk can export the CSVs it already reads (Reservas / Clientes /
      // Recurrentes / Actividad). Owner can revoke in Personal → Permisos.
      "exportData",
    ],
    priority: 2,
    isSystem: true,
    color: "#66BB6A",
    permissionsCustomized: false,
    permissionsSchemaVersion: CURRENT_PERMISSIONS_SCHEMA_VERSION,
  },
  {
    // 4th default system role (roadmap #101, decisions T2/T3 resolved
    // 2026-07-09). A thin, operational coach login: own class agenda +
    // students + attendance, NOTHING financial or administrative. The
    // restrictive grants (no deleteBooking / recordPayment / createSale /
    // viewIngresos / viewAnalytics / manage*) are enforced in business logic
    // via `can()` — the editable permission matrix stays out of Firestore
    // rules by design (rules keep only the coarse tenant / owner / CF-ledger
    // invariants). See the research doc §7 T3 + `00-roles-and-personas.md`.
    // Dark until the two-layer gate is live (`coachRoleLive`).
    id: "coach",
    name: "Entrenador",
    description:
      "Ve su agenda de clases y sus alumnos, marca asistencia. Sin facturación, analíticas ni borrado de reservas.",
    permissions: [
      "viewCalendar",
      "viewDashboard",
      "viewCrm",
      "createBooking",
      "cancelBooking",
      // Attendance is the coach's operational heartbeat — post-dates #101
      // (checkInBooking was split out of the booking write path later) and is
      // the future hook for bono-decrement + commission-by-attendance.
      "checkInBooking",
    ],
    priority: 3,
    isSystem: true,
    color: "#AB47BC",
    permissionsCustomized: false,
    permissionsSchemaVersion: CURRENT_PERMISSIONS_SCHEMA_VERSION,
  },
];

/** Lookup a default role by ID. Returns null if not a system role. */
export function defaultRoleById(id: string): ClubRole | null {
  return DEFAULT_CLUB_ROLES.find((r) => r.id === id) ?? null;
}

/**
 * Keys ADDED to each system role's DEFAULT permission set at each schema
 * version (vs the prior version). Drives the customised-role additive merge:
 * we add ONLY keys introduced strictly AFTER a customised doc's stored
 * version — never every currently-missing default, which would silently
 * re-grant a default the owner deliberately removed (audit H7). Must be kept
 * in lockstep with the `CURRENT_PERMISSIONS_SCHEMA_VERSION` bump log above:
 * whenever a system role gains a default key, add it here under the new
 * version for that role id.
 */
const PERMISSION_ADDITIONS_BY_VERSION: Record<
  number,
  Partial<Record<string, ClubPermission[]>>
> = {
  // v1 (2026-05-27) — money + ops keys added to the defaults.
  1: {
    owner: [
      "recordPayment",
      "issueRefund",
      "viewIngresos",
      "createSale",
      "refundSale",
      "manageCatalog",
      "checkInBooking",
    ],
    manager: [
      "recordPayment",
      "issueRefund",
      "viewIngresos",
      "createSale",
      "refundSale",
      "manageCatalog",
      "checkInBooking",
    ],
    // Receptionist got only the desk-level subset at v1 (NOT issueRefund /
    // refundSale / viewIngresos / manageCatalog — those stayed manager+).
    receptionist: ["recordPayment", "createSale", "checkInBooking"],
  },
  // v2 (2026-06-13) — receptionist default gains refund capability.
  2: {
    receptionist: ["issueRefund", "refundSale"],
  },
  // v3 (2026-06-22) — receptionist default gains CSV export.
  3: {
    receptionist: ["exportData"],
  },
};

/** Default keys introduced to `roleId` strictly after `storedVersion`. */
function permissionsIntroducedAfter(
  roleId: string,
  storedVersion: number,
): ClubPermission[] {
  const out = new Set<ClubPermission>();
  for (let v = storedVersion + 1; v <= CURRENT_PERMISSIONS_SCHEMA_VERSION; v++) {
    for (const p of PERMISSION_ADDITIONS_BY_VERSION[v]?.[roleId] ?? []) {
      out.add(p);
    }
  }
  return [...out];
}

/** Order-insensitive equality of two permission lists. */
function samePermissionSet(a: ClubPermission[], b: ClubPermission[]): boolean {
  if (a.length !== b.length) return false;
  const seen = new Set(a);
  return b.every((p) => seen.has(p));
}

/**
 * Reconcile a stored system-role doc against its code-level default
 * (`DEFAULT_CLUB_ROLES`). The behaviour depends on whether the owner has ever
 * customised the role:
 *
 * - **Untouched** (`permissionsCustomized === false`): the doc must equal the
 *   default set EXACTLY — missing keys are added *and* stray keys are stripped.
 *   This is what stops a stale seed (e.g. a receptionist still carrying
 *   `viewAnalytics` from an older default) from silently over-permissioning.
 * - **Customised** (`permissionsCustomized === true`): strictly additive —
 *   only keys the owner has never seen (introduced by a later
 *   `CURRENT_PERMISSIONS_SCHEMA_VERSION` bump) are added; their explicit
 *   grants/removals are preserved. Skipped once the doc is already at the
 *   current version, so a deliberate removal is never re-added.
 *
 * Non-system or unknown role IDs return unchanged. The caller persists +
 * version-bumps when `changed` is true (Firestore rules allow only owner/admin
 * to write `roles/{id}`, so non-owner sessions reconcile in-memory only).
 */
export function reconcileSystemRolePermissions(
  role: ClubRole,
): { merged: ClubRole; changed: boolean } {
  if (!role.isSystem) return { merged: role, changed: false };
  const defaults = defaultRoleById(role.id);
  if (!defaults) return { merged: role, changed: false };

  if (!role.permissionsCustomized) {
    // Untouched → snap to the canonical default set (add missing + strip stray).
    const alreadyCurrent =
      samePermissionSet(role.permissions, defaults.permissions) &&
      role.permissionsSchemaVersion === CURRENT_PERMISSIONS_SCHEMA_VERSION;
    if (alreadyCurrent) return { merged: role, changed: false };
    return {
      merged: {
        ...role,
        permissions: [...defaults.permissions],
        permissionsSchemaVersion: CURRENT_PERMISSIONS_SCHEMA_VERSION,
      },
      changed: true,
    };
  }

  // Customised → strictly additive, and only while behind the current version.
  if (role.permissionsSchemaVersion >= CURRENT_PERMISSIONS_SCHEMA_VERSION) {
    return { merged: role, changed: false };
  }
  const have = new Set(role.permissions);
  // Add ONLY keys introduced AFTER the doc's stored version — NOT every
  // currently-missing default. Adding all missing defaults silently re-granted
  // a default the owner had deliberately removed at the prior version (audit
  // H7): the reconciler has no other record of intent, so "missing" conflated
  // "new this version" with "owner removed it". `permissionsIntroducedAfter`
  // is that record. The `defaults.permissions.includes` guard keeps us from
  // adding a key that's listed in the additions map but is no longer a default.
  const toAdd = permissionsIntroducedAfter(
    role.id,
    role.permissionsSchemaVersion,
  ).filter((p) => !have.has(p) && defaults.permissions.includes(p));
  return {
    merged: {
      ...role,
      permissions: toAdd.length ? [...role.permissions, ...toAdd] : role.permissions,
      // Advance the watermark even when nothing was added, so the doc stops
      // reconciling on every sign-in once it's caught up to CURRENT.
      permissionsSchemaVersion: CURRENT_PERMISSIONS_SCHEMA_VERSION,
    },
    changed: true,
  };
}
